import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../store/services/user.service';

declare const gapi: any;
declare const google: any;

export interface GooglePickerConfig {
  accept: string | string[];
  maxFiles: number;
  maxFileSize: number;
}

const GOOGLE_SHEETS_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const MIME_TYPES: Record<string, string> = {
  '.csv': 'text/csv',
  '.xlsx': XLSX_MIME_TYPE,
  '.xls': 'application/vnd.ms-excel',
  '.txt': 'text/plain',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.json': 'application/json',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
};

const scripts: Record<string, Promise<void>> = {};

// Each uploader owns its picker, pending downloads and in-memory OAuth token.
@Injectable()
export class GooglePickerService {
  private ready: Promise<void>;
  private clientId: string;
  private token: string;
  private tokenExpiresAt = 0;
  private picker: any;
  private abortController: AbortController;
  private cancelSelection: () => void;

  constructor(private userService: UserService) {}

  isConfigured(): boolean {
    return !!(environment.googleApiKey && environment.googleAppId);
  }

  initialize(): Promise<void> {
    if (!this.ready) {
      this.ready = this.loadClientId().then(() => Promise.all([
        this.loadScript('https://apis.google.com/js/api.js', typeof gapi !== 'undefined'),
        this.loadScript('https://accounts.google.com/gsi/client',
          typeof google !== 'undefined' && !!google.accounts?.oauth2),
      ])).then(() => new Promise<void>((resolve, reject) => {
        gapi.load('picker', {
          callback: resolve,
          onerror: () => reject(new Error('Unable to load Google Drive. Please try again.')),
          timeout: 15000,
          ontimeout: () => reject(new Error('Google Drive took too long to load. Please try again.')),
        });
      })).catch(error => {
        this.ready = undefined;
        throw error;
      });
    }
    return this.ready;
  }

  private loadClientId(): Promise<void> {
    this.clientId = undefined;
    // Use the same backend configuration as Google sign-in, with no legacy client fallback.
    return this.userService.getSsoConfig().toPromise().then(config => {
      const clientId = config?.google?.client_id?.trim();
      if (!clientId) {
        throw new Error('Google Drive is unavailable because Google sign-in is not configured.');
      }
      this.clientId = clientId;
    }, () => {
      throw new Error('Unable to load Google sign-in settings. Please try again.');
    });
  }

  private loadScript(src: string, loaded: boolean): Promise<void> {
    if (loaded) {
      return Promise.resolve();
    }
    if (!scripts[src]) {
      scripts[src] = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        const timeout = window.setTimeout(() => fail(), 15000);
        const fail = () => {
          window.clearTimeout(timeout);
          script.remove();
          reject(new Error('Unable to load Google Drive. Please check your connection and try again.'));
        };
        script.src = src;
        script.async = true;
        script.onload = () => {
          window.clearTimeout(timeout);
          resolve();
        };
        script.onerror = fail;
        document.head.appendChild(script);
      }).catch(error => {
        delete scripts[src];
        throw error;
      });
    }
    return scripts[src];
  }

  // Call after initialize(), directly from a click so browsers allow the OAuth popup.
  createPicker(config: GooglePickerConfig): Promise<File[]> {
    if (!this.clientId) {
      return Promise.reject(new Error('Google Drive is not ready. Please try again.'));
    }
    this.close();
    return new Promise<File[]>((resolve, reject) => {
      let finished = false;
      const finish = (files: File[] = [], error?: Error) => {
        if (finished) {
          return;
        }
        finished = true;
        this.closePicker();
        this.cancelSelection = undefined;
        error ? reject(error) : resolve(files);
      };
      this.cancelSelection = () => finish();
      const showPicker = () => {
        if (finished) {
          return;
        }
        try {
          const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(false);
          const accepted = (Array.isArray(config.accept) ? config.accept : [config.accept])
            .join(',').split(',').map(value => value.trim().toLowerCase()).filter(Boolean);
          const mimeTypes = accepted.map(value => MIME_TYPES[value] || (value.includes('/') ? value : ''));
          if (this.canExportSheets(config.accept)) {
            mimeTypes.push(GOOGLE_SHEETS_MIME_TYPE);
          }
          // Unknown extensions/wildcards must not hide otherwise valid files.
          if (accepted.length && mimeTypes.every(value => value && !value.includes('*'))) {
            view.setMimeTypes(mimeTypes.join(','));
          }
          const builder = new google.picker.PickerBuilder()
            .addView(view)
            .setDeveloperKey(environment.googleApiKey)
            .setAppId(environment.googleAppId)
            .setOrigin(window.location.origin)
            .setOAuthToken(this.token)
            .setCallback((data: any) => {
              if (data.action === google.picker.Action.CANCEL) {
                finish();
              } else if (data.action === google.picker.Action.PICKED) {
                this.downloadFiles(data.docs, config).then(files => finish(files), error => finish([], error));
              }
            });
          if (!config.maxFiles || config.maxFiles > 1) {
            builder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
          }
          if (config.maxFiles) {
            builder.setMaxItems(config.maxFiles);
          }
          this.picker = builder.build();
          this.picker.setVisible(true);
        } catch (error) {
          finish([], error);
        }
      };

      if (this.token && Date.now() < this.tokenExpiresAt) {
        showPicker();
        return;
      }
      const scope = 'https://www.googleapis.com/auth/drive.file';
      try {
        const client = google.accounts.oauth2.initTokenClient({
          client_id: this.clientId,
          scope,
          callback: (response: any) => {
            if (finished) {
              return;
            }
            if (response.error || !response.access_token ||
              !google.accounts.oauth2.hasGrantedAllScopes(response, scope)) {
              finish([], new Error('Google Drive access was not granted. Please try again.'));
              return;
            }
            this.token = response.access_token;
            this.tokenExpiresAt = Date.now() + Math.max(0, Number(response.expires_in) - 60) * 1000;
            showPicker();
          },
          error_callback: (error: { type: string }) => {
            if (error.type === 'popup_closed') {
              finish();
            } else {
              finish([], new Error('Unable to open Google sign-in. Please allow popups and try again.'));
            }
          },
        });
        client.requestAccessToken({ prompt: '' });
      } catch (error) {
        finish([], error);
      }
    });
  }

  private canExportSheets(accept: string | string[]): boolean {
    const accepted = (Array.isArray(accept) ? accept : [accept])
      .join(',').split(',').map(value => value.trim().toLowerCase()).filter(Boolean);
    return !accepted.length || accepted.some(value =>
      ['.xlsx', XLSX_MIME_TYPE, 'application/*', '*/*'].includes(value),
    );
  }

  private async downloadFiles(docs: any[], config: GooglePickerConfig): Promise<File[]> {
    if (config.maxFiles && docs.length > config.maxFiles) {
      throw new Error(`Please select a maximum of ${config.maxFiles} file(s).`);
    }
    const controller = new AbortController();
    this.abortController = controller;
    const files: File[] = [];
    for (const doc of docs) {
      const isGoogleSheet = doc.mimeType === GOOGLE_SHEETS_MIME_TYPE;
      if (isGoogleSheet && !this.canExportSheets(config.accept)) {
        throw new Error('Google Sheets can only be imported when XLSX files are accepted.');
      }
      if (!isGoogleSheet && doc.mimeType?.startsWith('application/vnd.google-apps.')) {
        throw new Error('Please select a Google Sheet or an uploaded file, such as a CSV or Excel file.');
      }
      // A native sheet's metadata size is not the size of its exported workbook.
      if (!isGoogleSheet && config.maxFileSize && Number(doc.sizeBytes) > config.maxFileSize) {
        throw new Error(`${doc.name} exceeds the maximum file size.`);
      }
      const headers = new Headers({ Authorization: `Bearer ${this.token}` });
      if (doc.resourceKey) {
        headers.set('X-Goog-Drive-Resource-Keys', `${doc.id}/${doc.resourceKey}`);
      }
      const fileUrl = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(doc.id)}`;
      const downloadUrl = isGoogleSheet
        ? `${fileUrl}/export?mimeType=${encodeURIComponent(XLSX_MIME_TYPE)}`
        : `${fileUrl}?alt=media&supportsAllDrives=true`;
      // Fetch preserves the bytes of both spreadsheets and text files and bypasses app auth interceptors.
      const response = await fetch(
        downloadUrl,
        { headers, signal: controller.signal },
      );
      if (!response.ok) {
        if (response.status === 401) {
          this.token = undefined;
          throw new Error('Google Drive access expired. Please select your file again.');
        }
        throw new Error(`Unable to ${isGoogleSheet ? 'export' : 'download'} ${doc.name} from Google Drive (${response.status}).`);
      }
      const blob = await response.blob();
      if (controller.signal.aborted) {
        return [];
      }
      if (config.maxFileSize && blob.size > config.maxFileSize) {
        throw new Error(`${doc.name} exceeds the maximum file size.`);
      }
      const fileName = isGoogleSheet ? doc.name.replace(/\.xlsx?$/i, '') + '.xlsx' : doc.name;
      files.push(new File([blob], fileName, { type: isGoogleSheet ? XLSX_MIME_TYPE : doc.mimeType || blob.type }));
    }
    return files;
  }

  close(): void {
    if (this.cancelSelection) {
      this.cancelSelection();
    }
    this.closePicker();
  }

  private closePicker(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    if (this.picker) {
      this.picker.dispose();
      this.picker = undefined;
    }
  }
}
